'use client';
/* eslint-disable tailwindcss/no-arbitrary-value, tailwindcss/no-custom-classname -- CMS-1818 matches the prototype's fixed type scale and spacing. */

import { CalculationScope } from '@hyphengroup/cms-components/calculatedAttributes/store';
import { useCalculatedAttributeResolver } from '@hyphengroup/cms-components/calculatedAttributes/useCalculatedAttributeResolver';
import { useCtaResolver } from '@hyphengroup/cms-components/components/ApplyCta';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import {
  useEmailCaptureApi,
  useEmailCapturePayload,
} from '@hyphengroup/cms-components/components/EmailCapture';
import { Modal } from '@hyphengroup/cms-components/components/Modal';
import { ATTR_NAMES } from '@hyphengroup/cms-components/types/attrNames';
import { currencyFormatterNoDecimal } from '@hyphengroup/cms-components/utils/number';
import { tr } from '@hyphengroup/cms-components/utils/translations/tr';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getMonthlyRepayment, hasUsableEmail } from './helpers';
import { EmailCaptureConsentText, EmailCaptureStaticText } from './i18n';
import {
  trackClose,
  trackConsentToggle,
  trackEmailFieldFill,
  trackFormSubmit,
  trackSkip,
} from './tracking';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import { feApiFrontClient } from '$/(cms)/_modules/feApiClient/feApiFrontClient';
import Button from '$/components/Button';
import { useIdentity } from '$/modules/auth/hooks';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';
import AlphaCheckbox from '$/modules/material-tailwind/AlphaCheckbox';

/**
 * Skippable email-capture modal shown between a PL Apply CTA and the lender
 * (CMS-1818).
 *
 * `<ApplyCtaLink>` opens it for PL products carrying `EMAIL_CAPTURE` and hands
 * over the product and its block's calculation slot. The dialog reads that slot,
 * so the summary row shows the same figures the user saw on the card.
 *
 * It never blocks: submit, skip and close all end at the lender, at the very
 * destination the CTA would have used. Mounted through `<CmsFeaturesProvider>`.
 */
export const EmailCaptureModal: React.FC = () => {
  const moduleId = useEmailCapturePayload()?.moduleId;
  return moduleId ? (
    <CalculationScope moduleId={moduleId}>
      <EmailCaptureDialog />
    </CalculationScope>
  ) : (
    <EmailCaptureDialog />
  );
};

const EmailCaptureDialog: React.FC = () => {
  const payload = useEmailCapturePayload();
  const emailCaptureApi = useEmailCaptureApi();
  const context = useClientContext();
  const staticText = getClientTranslations(EmailCaptureStaticText, context);
  const consentText = getClientTranslations(EmailCaptureConsentText, context);
  const identity = useIdentity();
  const { settings } = usePageDataContext();
  const resolveCtaUrl = useCtaResolver();
  const resolveCalculated = useCalculatedAttributeResolver();

  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [isSent, setIsSent] = useState(false);
  // The email `fill` event (#2461) fires only on the first real edit per open,
  // not on the logged-in prefill (which sets state without an onChange).
  const emailFillTracked = useRef(false);

  const product = payload?.product;
  const loanParams = payload?.loanParams;
  const isOpen = payload != null;
  const providerName = product?.provider?.provider_name ?? '';

  // Every Apply click opens a fresh form (§9): reset the fields and prefill the
  // email for logged-in users, who can still type over it. Keyed on the payload
  // itself, not on `isOpen` — after a submit the modal stays up on the
  // confirmation, so the next Apply click never flips `isOpen` and would
  // otherwise re-show that stale confirmation.
  useEffect(() => {
    if (!payload) return;
    setEmail(identity?.email ?? '');
    setConsent(false);
    setConsentError(false);
    setIsSent(false);
    emailFillTracked.current = false;
  }, [payload, identity?.email]);

  /**
   * Hands the user over to the lender — the same destination the Apply CTA would
   * have used, resolved by the same resolver, and in a new tab as that CTA does
   * (every apply CTA renders with `target="_blank"`). The modal is only a gate in
   * front of the existing PL flow, so the handoff itself is unchanged.
   *
   * Must stay synchronous inside the click handler: awaiting anything first would
   * spend the user activation and the pop-up blocker would swallow the new tab.
   */
  const redirectToLender = useCallback(() => {
    const ctaUrl = product ? resolveCtaUrl({ product, loanParams }) : undefined;

    if (!ctaUrl) {
      // Nothing to hand off to (the CTA would not have rendered either) — close
      // rather than leaving the user stuck here.
      emailCaptureApi?.close();
      return;
    }

    // `noopener` severs the handle on purpose, so this returns null even when the
    // tab did open — there is nothing to test for a blocked pop-up. Same call as
    // every other imperative CTA in the codebase (see `FeLink`).
    window.open(ctaUrl, '_blank', 'noopener,noreferrer');
  }, [product, loanParams, resolveCtaUrl, emailCaptureApi]);

  // Close (X / backdrop / Esc) and Skip both leave without an email and both
  // redirect, but the tracking plan gives them distinct events (#2463 vs #2464).
  const leaveWithoutEmail = useCallback(() => {
    redirectToLender();
    // Nothing was submitted, so there is nothing to stay open for.
    emailCaptureApi?.close();
  }, [redirectToLender, emailCaptureApi]);

  const handleClose = useCallback(() => {
    trackClose();
    leaveWithoutEmail();
  }, [leaveWithoutEmail]);

  const handleSkip = useCallback(() => {
    trackSkip(staticText.skip);
    leaveWithoutEmail();
  }, [leaveWithoutEmail, staticText.skip]);

  const handleSubmit = useCallback(() => {
    // The CTA stays enabled; pressing it without consent only shows the error
    // and goes nowhere (§4).
    if (!consent) {
      setConsentError(true);
      return;
    }

    if (!hasUsableEmail(email)) {
      // Consent without a usable address is never stored (§5). The analytics
      // plan has no event for this path, so it just redirects.
      leaveWithoutEmail();
      return;
    }

    setIsSent(true);

    // §17 capture: lead draft + CRM message + raw-email Segment forward, all
    // server-side (`form_submit` below stays PII-safe per #2465). Fire and
    // forget — no failure may block the lender handoff (§25); the route logs.
    void feApiFrontClient
      .submitEmailCaptureLead({
        email,
        rewardId: product?.reward_id,
        productUid: product?.entryUID ?? product?.uid,
        productName: product?.product_name ?? product?.title,
        providerName: product?.provider?.provider_name,
        loanAmount: loanParams?.amount,
        loanTenure: loanParams?.tenure,
        searchParams: window.location.search,
      })
      .catch(() => undefined);

    trackFormSubmit({
      email,
      item: {
        // Mirrors `sendAnalytics`: item_id is the product title.
        itemId: product?.title ?? '',
        itemName: product?.product_name ?? '',
        itemBrand: product?.provider?.provider_name ?? '',
        // The payload carries no list position; single-product context → '0'.
        index: '0',
      },
    });

    // The confirmation (§6) is what the user comes back to in this tab, not a
    // wait in front of the redirect.
    redirectToLender();
  }, [
    consent,
    email,
    product,
    loanParams,
    redirectToLender,
    leaveWithoutEmail,
  ]);

  if (!isOpen) return null;

  const monthlyRepayment = getMonthlyRepayment(
    product,
    context.currencyDisplay,
    resolveCalculated(ATTR_NAMES.MONTHLY_REPAYMENT, product),
  );
  const summaryLine =
    loanParams &&
    tr(
      monthlyRepayment ? 'summaryLine' : 'summaryLineWithoutRepayment',
      staticText,
      {
        amount: `${context.currencyDisplay}${currencyFormatterNoDecimal.format(Number(loanParams.amount))}`,
        tenure: loanParams.tenure,
        monthlyRepayment: monthlyRepayment ?? '',
      },
    );

  return (
    <Modal
      isOpen
      onClose={handleClose}
      sheetOnMobile
      // Material Tailwind's `md` size also sets a percentage min-width, so that
      // has to be cleared or the panel stays wider than the design's 560px.
      className="md:!w-[560px] md:!min-w-0 md:!max-w-[560px] md:!rounded-2xl"
    >
      <div className="flex flex-col gap-4 p-5 md:p-7">
        {isSent ? (
          <div className="flex min-h-60 flex-col items-center justify-center gap-5 text-center">
            <MaterialIcon
              icon="check_circle"
              px={48}
              className="text-highlights/green-dark"
            />
            <p className="font-bold text-text/primary text-[19px] leading-[25px]">
              {tr('confirmation', staticText, { provider: providerName })}
            </p>
          </div>
        ) : (
          <>
            {settings?.site_logo?.url ? (
              // Brand mark above the eyebrow, per the prototype's content order.
              <CmsImage
                alt={settings.site_logo.alternativeText || 'MoneyHero'}
                src={settings.site_logo.url}
                width={140}
                height={24}
                sizes={{ _: 140 }}
                className="h-6 w-auto self-start object-contain"
              />
            ) : null}

            <div className="flex flex-col gap-1">
              <p className="font-bold uppercase tracking-[0.04em] text-text/link text-[13px] leading-[22px]">
                {tr('eyebrow', staticText, {
                  provider: providerName.toUpperCase(),
                })}
              </p>
              <p className="font-bold text-text/primary text-[19px] leading-[25px]">
                {staticText.title}
              </p>
            </div>

            {summaryLine ? (
              <div className="flex items-start gap-3 rounded-xl px-4 py-3.5 bg-fill/primary-light">
                <MaterialIcon
                  icon="attach_money"
                  px={20}
                  className="mt-px shrink-0 text-text/link"
                />
                <div className="flex flex-col gap-0.5">
                  <p className="font-semibold text-text/link text-[13px] leading-[22px]">
                    {staticText.summaryLabel}
                  </p>
                  <p className="font-normal text-text/secondary text-sm leading-[19.6px]">
                    {summaryLine}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <label
                className="font-semibold text-text/primary text-[13px] leading-[22px]"
                htmlFor="pl-email-capture-email"
              >
                {staticText.emailLabel}
              </label>
              <input
                autoComplete="email"
                className="w-full rounded-lg border border-borders/default px-3.5 py-3 bg-fill/card text-text/primary text-[15px] leading-5 placeholder:text-text/disabled focus:border-text/link focus:outline-none"
                id="pl-email-capture-email"
                name="email"
                onChange={(event) => {
                  // #2461 — the first real edit per open counts as the fill.
                  if (!emailFillTracked.current) {
                    emailFillTracked.current = true;
                    trackEmailFieldFill();
                  }
                  setEmail(event.target.value);
                }}
                placeholder={staticText.emailPlaceholder}
                type="email"
                value={email}
              />
            </div>

            <div className="flex flex-col gap-2">
              {/* The error sits below this box, not inside it, as in the prototype. */}
              <div
                className={
                  consentError
                    ? 'flex items-start gap-2.5 rounded-lg border border-highlights/red-dark p-3 bg-highlights/red-light'
                    : 'flex items-start gap-2.5'
                }
              >
                <AlphaCheckbox
                  checked={consent}
                  inputProps={{ 'aria-label': staticText.consentAriaLabel }}
                  onChange={(checked: boolean) => {
                    trackConsentToggle(checked); // #2462
                    setConsent(checked);
                    if (checked) setConsentError(false);
                  }}
                />
                <span className="text-text/secondary text-xs leading-[17px]">
                  {consentText.consent}
                </span>
              </div>
              {consentError ? (
                <p className="flex items-center gap-1.5 font-semibold text-highlights/red-dark text-[13px] leading-4">
                  <MaterialIcon icon="warning" px={15} />
                  {staticText.consentError}
                </p>
              ) : null}
            </div>

            <Button
              // `h-auto min-h-12` + vertical padding lets the long PL CTA label
              // wrap to a second line on a narrow bottom sheet instead of being
              // clipped by the button's fixed height + `text-nowrap` base style
              // (CMS-1818 mobile QA). Desktop keeps its single line and 48px height.
              className="h-auto min-h-12 w-full py-2.5"
              styleType="cta"
              onClick={handleSubmit}
              type="button"
            >
              <span className="flex items-center justify-center gap-2 whitespace-normal text-center font-bold text-base leading-5 md:text-xl">
                {tr('submit', staticText, { provider: providerName })}
                <MaterialIcon icon="open_in_new" px={18} className="shrink-0" />
              </span>
            </Button>

            <button
              className="w-full font-bold text-text/link text-[15px] leading-5"
              onClick={handleSkip}
              type="button"
            >
              {staticText.skip}
            </button>

            <p className="flex items-center justify-center gap-1.5 text-text/secondary-lighter text-xs leading-4">
              <MaterialIcon icon="lock" px={13} />
              {staticText.assurance}
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};
